// src/v2/components/modules/UploadingSchedule/components/UploadingScheduleTable.js
import React, { useState, useMemo } from "react";
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { CreateTableNew as CreateTable } from "../../../../../common/APICommon";
import { FEATURES } from "../../../../../config/features";

const UploadingScheduleTable = ({
  data,
  dateRange,
  allShifts,
  editingRowId,
  setEditingRowId,
  handleCellChange,
  apiFilterPayload
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // ─── DYNAMIC HEADERS ──────────────────────────────────────────
  const { headers, accessors } = useMemo(() => {
    const baseHeaders = ["ID", "Full Name"];
    const baseAccessors = ["employeeId", "fullName"];

    const dayHeaders = dateRange.map(dateStr => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: '2-digit',
        year: 'numeric'
      });
    });

    const dayAccessors = dateRange.map(dateStr => {
      const day = new Date(dateStr).getDate();
      return `day_${day}`;
    });

    return {
      headers: [...baseHeaders, ...dayHeaders, "Actions"],
      accessors: [...baseAccessors, ...dayAccessors, "actions"]
    };
  }, [dateRange]);

  // ─── CELL RENDERER ─────────────────────────────────────────────
  const customCellRenderer = (row, accessor) => {
    if (accessor.startsWith("day_")) {
      const dayKey = accessor.replace("day_", "");
      const currentShiftId = row.schedules?.[dayKey] || "";

      // REPLACED: Logic to determine what string to show based on Feature Flag
      const getShiftDisplay = (shift) => {
        if (!shift) return "-";
        return FEATURES.SHIFTSCHEDULE_CODE ? shift.code : shift.shiftName;
      };

      // EDIT MODE
      if (editingRowId === row.id) {
        return (
          <select
            // MODIFIED: Increased min-w from 80px to 140px if using names
            className={`text-[10px] border border-blue-300 rounded p-1 ${FEATURES.SHIFTSCHEDULE_CODE ? 'min-w-[80px]' : 'min-w-[140px]'} bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500 font-semibold cursor-pointer`}
            value={currentShiftId}
            onChange={(e) => handleCellChange(row.id, dayKey, e.target.value)}
          >
            <option value="">--</option>
            {allShifts.map(s => (
              <option key={s.id} value={s.id}>
                {/* MODIFIED: Toggle display label */}
                {FEATURES.SHIFTSCHEDULE_CODE ? s.code : s.shiftName}
              </option>
            ))}
          </select>
        );
      }

      // VIEW MODE
      const shift = allShifts.find(s => s.id.toString() === currentShiftId.toString());
      return (
        <span className={`text-[11px] font-bold ${shift ? 'text-blue-800' : 'text-gray-300'}`}>
          {/* MODIFIED: Toggle display label */}
          {getShiftDisplay(shift)}
        </span>
      );
    }
    return null;
  };

  const actions = [
    {
      tooltip: "Modify Schedule",
      icon: <EditIcon fontSize="small" />,
      color: "bg-blue-600",
      visible: (row) => editingRowId !== row.id,
      function: (row) => setEditingRowId(row.id),
      className: "hover:bg-blue-700"
    },
    {
      tooltip: "Cancel Modification",
      icon: <CancelIcon fontSize="small" />,
      color: "bg-gray-500",
      visible: (row) => editingRowId === row.id,
      function: () => setEditingRowId(null),
      className: "hover:bg-gray-600"
    }
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <CreateTable
        tableName="UploadingScheduleTable"
        headers={headers}
        accessors={accessors}
        data={data}
        actions={actions}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={10}
        totalItems={data.length}
        customCellRenderer={customCellRenderer}
        emptyMessage={
          !apiFilterPayload.PayrollPeriodId
            ? "Select a period and click Generate to load data."
            : "No matching employees found."
        }
      />
    </div>
  );
};

export default UploadingScheduleTable;